import { faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons';
import type { ReactNode } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { logErrorToSentry } from '../lib/log-error-to-sentry';
import { ErrorFallback } from './error-fallback';

const GenericErrorFallback = ({ xid }: { xid: string }) => {
	const { t } = useTranslation();

	return (
		<ErrorFallback
			xid={xid}
			icon={faTriangleExclamation}
			title={t('errors.generic.title', 'Something went wrong')}
			message={t(
				'errors.generic.message',
				"We've encountered an unexpected error. Our team has been notified and will investigate the issue.",
			)}
			errorReferenceLabel={t('errors.error-reference', 'Error Reference')}
			errorReferenceDescription={t(
				'errors.provide-code-when-reporting',
				'Please provide this code when reporting the issue',
			)}
		/>
	);
};

export const GenericErrorBoundary = ({ children }: { children: ReactNode }) => {
	return (
		<ErrorBoundary
			fallbackRender={(props) => {
				// Log to Sentry with XID. Reporting the same error twice returns the
				// same XID and captures only once, so calling this on re-render is safe.
				const xid = logErrorToSentry({
					error: props.error,
					context: 'GenericErrorBoundary',
					level: 'error',
				});

				return <GenericErrorFallback xid={xid} />;
			}}
			onReset={() => {
				// Reset app state if needed
				window.location.reload();
			}}
		>
			{children}
		</ErrorBoundary>
	);
};
